import * as Sentry from '@sentry/react'
import { AppDispatch } from 'App'
import { RegistrationRequest, RegistrationRequestCrmTagEnum } from 'clients/quiz-service'
import {
  cookieDomainForHost,
  QUIZ_COUNTRY_COOKIE_NAME,
  QUIZ_EMAIL_COOKIE_NAME,
  QUIZ_ID_COOKIE_EXPIRY_DAYS,
  QUIZ_ID_COOKIE_NAME,
  QUIZ_VERSION_COOKIE_NAME,
} from 'components/constants'
import {
  trackQuizLanded,
  trackQuizQuestionAnswered,
  trackQuizScreenViewed,
  trackQuizSignUp,
  trackQuizStarted,
  trackQuizUserClick,
} from 'components/gtmV2'
import { EntryScreen } from 'components/screen-templates/EntryScreen'
import { FormValues } from 'components/screen-templates/FormScreen'
import { formValuesToAnswers } from 'components/utils'
import { useQuizService } from 'contexts/QuizServiceContext'
import queryString from 'query-string'
import React, { useEffect, useRef } from 'react'
import { useCookies } from 'react-cookie'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect, Route, Switch, useLocation } from 'react-router-dom'
import {
  Answer,
  selectQuiz,
  selectQuizCountry,
  submitAnswers,
  updateQuiz,
  UpdateQuizAction,
  updateQuizCountry,
} from 'redux/quiz/slice'
import { Menoscale, QuizBase, Quizzes } from 'redux/quiz/types'
import { UAParser } from 'ua-parser-js'
import { geoQuizCountry } from '../../../libs/geoLocation'
import { HeaderBar } from '../../screen-components/HeaderBar'
import { ScreenShell } from '../../core/layout/ScreenShell'
import { createQuizScreens } from './createQuizScreens'
import { useNavigator } from './useNavigator'
import { LegacyQuizScreen } from '../../../config/types'
import {
  calculateMenoscaleScore,
  getMenoscaleMainSymptoms,
  getNormalisedMenoscaleSymptoms,
} from '../../../libs/menoscale/utils'
import { useExperiments } from '../../../contexts/ExperimentContext'
import { usePrefetchNextImage } from '../../../hooks/usePrefetchNextImage'
import { getQuizRouteConfig } from './quizRouting'
import DEFAULT_QUIZ_CONFIG from '../../../config/quiz.config'

const cookieExpiryDate = new Date(Date.now() + QUIZ_ID_COOKIE_EXPIRY_DAYS * 24 * 60 * 60 * 1000)

const sanitisedAnswer = (answer: Answer) => {
  return Array.isArray(answer.answer) ? answer.answer.toString() : answer.answer
}

const SentryRoute = Sentry.withSentryRouting(Route)

type MenoscaleData = {
  menoscaleScienceConsent: boolean
  menoscaleScore: number
  menoscaleMainSymptoms: string[]
  answers: Answer[]
}

export const ScreenRoutes: React.FC = () => {
  const [cookies, setCookie] = useCookies([
    QUIZ_COUNTRY_COOKIE_NAME,
    QUIZ_ID_COOKIE_NAME,
    QUIZ_VERSION_COOKIE_NAME,
    QUIZ_EMAIL_COOKIE_NAME,
    'ENABLE_QUIZ_DEV_TOOLS',
  ])
  const location = useLocation()
  const { quizVersion, routeBase } = getQuizRouteConfig(location.pathname)
  const productType = DEFAULT_QUIZ_CONFIG[quizVersion]?.productType
  const { userApi, quizApi } = useQuizService()
  const dispatch = useDispatch<AppDispatch>()
  const quiz = useSelector(selectQuiz)
  const quizCountry = useSelector(selectQuizCountry)
  const hasQuizStartedFired = useRef(false)

  const fireQuizStartedOnce = () => {
    if (hasQuizStartedFired.current) return
    hasQuizStartedFired.current = true
    trackQuizStarted(quizVersion, productType)
  }
  const { getActiveExperiments } = useExperiments()

  const cookieDomain = cookieDomainForHost(window.location.hostname)

  const dispatchQuizCountryUpdate = (country: string) => {
    dispatch(updateQuizCountry({ quiz_country: country }))

    setCookie(QUIZ_COUNTRY_COOKIE_NAME, country, {
      path: '/',
      expires: cookieExpiryDate,
      domain: cookieDomain,
    })
  }

  const setQuizCountry = async () => {
    const country = await geoQuizCountry(cookies[QUIZ_COUNTRY_COOKIE_NAME])

    if (!country) {
      return
    }

    dispatchQuizCountryUpdate(country)
  }

  const updateQuizAnswersFromBackend = async (userId: string) => {
    const quizResponse = await quizApi.getQuiz(userId)
    const quizResponseAnswers = Object.entries(quizResponse.data.answers)
    const answers = Object.fromEntries(
      quizResponseAnswers
        .filter(([key]) => key !== 'weight' && key !== 'ideal_weight')
        .map(([key, value]) => {
          return [key, value]
        }),
    )

    // TODO remove this after migrating weight/ideal_weight to weight_lbs/ideal_weight_lbs.
    if (quizResponseAnswers.length !== answers.length) {
      const legacyWeight = quizResponseAnswers.find(([key]) => key == 'weight')
      const weightLbs = quizResponseAnswers.find(([key]) => key == 'weight_lbs')
      const weightKg = quizResponseAnswers.find(([key]) => key == 'weight_kg')
      const legacyIdealWeight = quizResponseAnswers.find(([key]) => key == 'ideal_weight')
      const idealWeightLbs = quizResponseAnswers.find(([key]) => key == 'ideal_weight_lbs')
      const idealWeightKg = quizResponseAnswers.find(([key]) => key == 'ideal_weight_kg')

      if (!weightLbs && !weightKg && legacyWeight) {
        answers.weight_lbs = legacyWeight[1]
        answers.weight_unit = 'lbs'
      }

      if (!idealWeightLbs && !idealWeightKg && legacyIdealWeight) {
        answers.ideal_weight_lbs = legacyIdealWeight[1]
      }
    }

    const answersUpdate: UpdateQuizAction = {
      userId: userId,
      version: quizVersion,
      quiz: answers,
    }

    await dispatch(updateQuiz(answersUpdate))

    if (quizResponse.data.quiz_country) {
      dispatchQuizCountryUpdate(quizResponse.data.quiz_country)
    } else {
      await setQuizCountry()
    }
  }

  useEffect(() => {
    const { user_id: urlUserId } = queryString.parse(location.search)
    const cookieUserId = cookies[QUIZ_ID_COOKIE_NAME]

    const userId = typeof urlUserId === 'string' ? urlUserId : cookieUserId

    trackQuizLanded(quizVersion, productType, userId)

    if (!userId) {
      setQuizCountry()
      return
    }

    updateQuizAnswersFromBackend(userId)
      .then(() => {
        setCookie(QUIZ_ID_COOKIE_NAME, userId, { path: '/', expires: cookieExpiryDate, domain: cookieDomain })
      })
      .catch((e) => {
        console.log(`Error: failed to retrieve quiz answers for ${userId}`)
        Sentry.captureException(e)
      })
  }, [])

  const onFormSubmit = async (values?: FormValues, goalAnswers?: Answer[]) => {
    fireQuizStartedOnce()
    trackQuizUserClick(quizVersion, productType, 'cta', { pageName: location.pathname })
    if (values) {
      let answers = formValuesToAnswers(values)
      if (goalAnswers) {
        answers = [...answers, ...goalAnswers]
      }

      const { payload: newerQuiz } = await dispatch(submitAnswers({ quiz_version: quizVersion, answers }))

      // Send all answers to new datalayer
      answers.forEach((answer) => {
        trackQuizQuestionAnswered(quizVersion, productType, answer.question_key, sanitisedAnswer(answer))
      })

      // Needed as there was some kind of race condition that meant we were not using
      // latest quiz answer to calculate next() route
      next(newerQuiz)
    } else {
      next()
    }
  }

  const onAdviceSubmit = (goalAnswers?: Answer[]): void => {
    fireQuizStartedOnce()
    if (goalAnswers) {
      dispatch(submitAnswers({ quiz_version: quizVersion, answers: [...goalAnswers] }))
    }

    next()
  }

  const registerUser = async (
    email: string,
    marketingConsent: boolean,
    quizAnswers: Quizzes,
    tagId?: string,
    crmTag?: string,
    healthGoal?: string,
    menoscaleScienceConsent?: boolean,
    menoscaleScore?: number,
    menoscaleMainSymptoms?: string[],
  ) => {
    const registrationData: RegistrationRequest = {
      id: quiz.id,
      email: email,
      quiz_version: quizVersion,
      marketing_consent: marketingConsent,
      health_goal: healthGoal,
      menoscale_science_consent: menoscaleScienceConsent,
      menoscale_score: menoscaleScore,
      menoscale_main_symptoms: menoscaleMainSymptoms,
      user_attributes: {
        country: quizCountry,
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        age_group: quizAnswers.welcome_age_range || quizAnswers.age_range,
        biological_sex: quizAnswers.biological_sex || quizAnswers.sex,
        health_data_consent: String((quizAnswers as Record<string, unknown>).data_consent) === 'true',
        health_goal: quizAnswers.goals,
      },
    }
    if (tagId) {
      registrationData['tag_id'] = tagId
    }

    if (crmTag) {
      registrationData['crm_tag'] = crmTag as RegistrationRequestCrmTagEnum
    }

    await userApi.register(registrationData)

    setCookie(QUIZ_ID_COOKIE_NAME, quiz.id, { path: '/', expires: cookieExpiryDate, domain: cookieDomain })
    setCookie(QUIZ_VERSION_COOKIE_NAME, quizVersion, { path: '/', expires: cookieExpiryDate, domain: cookieDomain })
    setCookie(QUIZ_EMAIL_COOKIE_NAME, email, { path: '/', expires: cookieExpiryDate, domain: cookieDomain })
  }

  const storeUserDeviceOS = async (userAgentString: string) => {
    const parser = new UAParser(userAgentString)
    const user_device_os = parser.getOS().name
    if (!user_device_os) {
      throw "Couldn't determine user device OS"
    }
    dispatch(
      submitAnswers({
        quiz_version: quizVersion,
        answers: [{ question_key: 'user_device_os', answer: user_device_os }],
      }),
    )
  }

  const hasScienceOptIn = (quiz: QuizBase): quiz is Menoscale => 'menoscale_science_consent' in quiz

  const submitMenoscaleAnswers = async (quizAnswers: Menoscale): Promise<MenoscaleData> => {
    const menoscaleScienceConsent = String(quizAnswers.menoscale_science_consent) === 'true'
    const menoscaleScore = calculateMenoscaleScore(quizAnswers as Menoscale)
    const menoscaleMainSymptoms = getMenoscaleMainSymptoms(quizAnswers as Menoscale).map((symptom) => symptom.name)
    const answers: Answer[] = [
      { question_key: 'menoscale_score', answer: String(menoscaleScore) },
      { question_key: 'menoscale_science_consent', answer: String(menoscaleScienceConsent) },
      { question_key: 'menoscale_main_symptoms', answer: menoscaleMainSymptoms },
    ]
    await dispatch(submitAnswers({ quiz_version: quizVersion, answers }))

    return { menoscaleScienceConsent, menoscaleScore, menoscaleMainSymptoms, answers }
  }
  const onEmailSubmit = async (values: FormValues, quizAnswers: Quizzes) => {
    fireQuizStartedOnce()
    trackQuizUserClick(quizVersion, productType, 'cta', { pageName: location.pathname })
    if (values) {
      const marketingConsent = values.marketingOptOut ? false : !!values.marketingOptIn

      try {
        let menoscaleData: MenoscaleData | null = null
        // we check if the menoscale_science_consent field to cast the quizAnswers to Menoscale
        if (quizVersion === 'menoscale' && hasScienceOptIn(quizAnswers)) {
          menoscaleData = await submitMenoscaleAnswers(quizAnswers)
          menoscaleData.answers?.forEach((answer) => {
            trackQuizQuestionAnswered(quizVersion, productType, answer.question_key, sanitisedAnswer(answer))
          })
        }

        await registerUser(
          values.email,
          marketingConsent,
          quizAnswers,
          values.tagId,
          values.crmTag,
          quizAnswers?.health_goal,
          menoscaleData?.menoscaleScienceConsent,
          menoscaleData?.menoscaleScore,
          getNormalisedMenoscaleSymptoms(menoscaleData?.menoscaleMainSymptoms, quizCountry),
        )
      } catch (error) {
        console.error('Could not register subscriber: ', error)
      }

      trackQuizSignUp(
        quizVersion,
        productType,
        quizVersion === 'menoscale' ? 'menoscale' : 'quiz',
        values.email,
        marketingConsent ? 'optin' : 'optout',
        values.crmTag,
      )

      try {
        await storeUserDeviceOS(window.navigator.userAgent)
      } catch (error) {
        console.error(`Failed storing user device OS: ${error}`)
      }
    }
    next()
  }

  const onNextClick = () => {
    fireQuizStartedOnce()
    trackQuizUserClick(quizVersion, productType, 'cta', { pageName: location.pathname })
    next()
  }
  const onBackClick = () => {
    fireQuizStartedOnce()
    trackQuizUserClick(quizVersion, productType, 'back_button', { pageName: location.pathname, linkPosition: 'header' })
    previous()
  }

  // removed useMemo wrapper as React was complaining of hooks within hooks
  const _screens: LegacyQuizScreen[] = createQuizScreens(
    quiz,
    quizVersion,
    getActiveExperiments(),
    quizCountry,
    onFormSubmit,
    onAdviceSubmit,
    onEmailSubmit,
    onNextClick,
    onBackClick,
  )

  const { previous, next, currentPageIndex, hideProgressBar } = useNavigator(_screens, quiz)

  useEffect(() => {
    if (currentPageIndex < 0) return
    trackQuizScreenViewed(quizVersion, productType, location.pathname)
  }, [location.pathname, currentPageIndex, quizVersion])

  usePrefetchNextImage(_screens, currentPageIndex)

  const quizAnswersKey = JSON.stringify(quiz[quizVersion])
  const screenComponents = React.useMemo(
    () =>
      _screens.map((screen) => (
        <Route
          key={screen.path}
          path={screen.path}
          render={() => <React.Fragment key={screen.path}>{screen.screenComponent}</React.Fragment>}
        />
      )),
    [_screens, quizAnswersKey],
  )

  const currentBackground = _screens[currentPageIndex]?.backgroundColor
  const currentOverlayHeader = _screens[currentPageIndex]?.overlayHeader
  const renderScreens = (): JSX.Element[] => screenComponents

  const header = (
    <SentryRoute
      path={`/${routeBase}`}
      render={() => (
        <>
          <HeaderBar
            quizVersion={quizVersion}
            currentPageIndex={currentPageIndex}
            hideProgressBar={hideProgressBar}
            overlayHeader={currentOverlayHeader}
            screens={_screens}
            onBackClick={currentPageIndex > 0 ? onBackClick : undefined}
          />
          {cookies.ENABLE_QUIZ_DEV_TOOLS === 'true' && (
            <pre
              style={{
                position: 'fixed',
                top: 0,
                overflow: 'scroll',
                height: '100vh',
                background: 'hsl(45.65deg 95.43% 57.06% / 5%)',
              }}
            >
              {JSON.stringify(quiz, null, 2)}
            </pre>
          )}
        </>
      )}
    />
  )

  return (
    <ScreenShell
      header={header}
      backgroundColor={currentBackground}
      phoneTypeScale={DEFAULT_QUIZ_CONFIG[quizVersion]?.phoneTypeScale}
    >
      <Switch>
        {/* Legacy redirect: the funnel previously lived at /e4bis/* */}
        <SentryRoute
          key="e4bis-redirect"
          path="/e4bis"
          render={({ location: loc }) => (
            <Redirect to={{ ...loc, pathname: loc.pathname.replace(/^\/e4bis/, '/e4') }} />
          )}
        />
        {renderScreens()}
        <SentryRoute
          key="fallback"
          path="/"
          render={(props) => <EntryScreen to={_screens[0].path} quizVersion={quizVersion} {...props} />}
        />
      </Switch>
    </ScreenShell>
  )
}
